import axios from 'axios'
import request from './request'
import { createMarketRequest } from './thirdPartyMarket'
import { getStockQuotes } from './stockQuotes'
import { quoteNumber } from './quoteDisplay'
import { marketSession } from './marketClock'

export const MARKET_REFRESH_MS = 10000
export async function marketRequest(kind, params = {}, initial = false) {
  if (initial) {
    const actions = { minute: 'indexMinute', 'us-minute': 'indexMinute', board: 'marketBoard', 'us-constituents': 'marketBoard' }
    if (!actions[kind]) throw new Error('没有已核对的行情首载接口')
    const result = await request({ url: 'vexe/v1/stock/' + actions[kind], method: 'get', params: { ...params, market: kind === 'board' ? 'hk' : 'us' } })
    if (!result) throw new Error('行情首载接口返回异常')
    return result
  }
  const config = createMarketRequest(kind, params)
  return (await axios({ ...config, timeout: 15000 })).data
}
export async function marketBackend(action, params = {}, method = 'get') {
  const value = await request({ url: 'vexe/v1/stock/' + action, method, ...(method === 'get' ? { params } : { data: params }) })
  if (!value) throw new Error('行情首载接口返回异常')
  return value
}
// One timer per region; failures keep existing data and do not trigger backend fallback.
export function isMarketOpen(market) {
  if (!['cn', 'hk', 'us'].includes(market)) return true
  return marketSession(market) === '交易中'
}
// The timer remains active during a closed session so it resumes automatically at the next open.
export function pollMarket(refresh, interval = MARKET_REFRESH_MS, market = '') {
  if (typeof interval === 'string' || typeof interval === 'function') { market = interval; interval = MARKET_REFRESH_MS }
  let busy = false, stopped = false, wakePending = false, hidden = document.hidden
  let lastRefresh = performance.now()
  let openMarkets = ['cn', 'hk', 'us'].filter(isMarketOpen)
  const timer = setInterval(async () => {
    const currentMarket = typeof market === 'function' ? market() : market
    const nextOpen = (currentMarket ? [currentMarket] : ['cn', 'hk', 'us']).filter(isMarketOpen)
    if (nextOpen.some(value => (!currentMarket || currentMarket === value) && !openMarkets.includes(value)) || (hidden && !document.hidden)) wakePending = true
    openMarkets = nextOpen; hidden = document.hidden
    if (stopped || busy || hidden || (currentMarket && !isMarketOpen(currentMarket))) return
    if (!wakePending && performance.now() - lastRefresh < interval) return
    wakePending = false; lastRefresh = performance.now()
    busy = true
    try { await refresh(() => stopped) } catch (_) { /* Retry the public source on the next tick. */ }
    finally { busy = false }
  }, Math.min(interval, 1000))
  return () => { stopped = true; clearInterval(timer) }
}
export function visibleMarketRows(element, rows, selector = 'tbody tr') {
  if (!element) return []
  const bounds = element.getBoundingClientRect()
  if (!bounds.width || !bounds.height) return []
  return Array.from(element.querySelectorAll(selector)).flatMap((node, index) => {
    const rect = node.getBoundingClientRect()
    return rows[index] && rect.bottom > bounds.top && rect.top < bounds.bottom ? [rows[index]] : []
  })
}
export function publicBody(result) {
  if (typeof result !== 'string') return result
  return JSON.parse(result.replace(/^[\w.]+\s*=\s*/, '').replace(/;\s*$/, ''))
}
export function marketSummary(result, market) {
  const body = publicBody(result)
  if (market === 'cn') {
    const value = body && body.data && body.data.ups_downs_dsb
    if (!value || !Array.isArray(value.detail)) throw new Error('市场分布格式异常')
    return value
  }
  const value = body && body.Result && body.Result.Result && body.Result.Result[0]
  const summary = value && value.DisplayData && value.DisplayData.resultData && value.DisplayData.resultData.tplData && value.DisplayData.resultData.tplData.result
  if (!summary || !summary.diagram || !Array.isArray(summary.diagram.diagram)) throw new Error('市场分布格式异常')
  return { diagram: summary.diagram.diagram, ratio: summary.ratio || {} }
}
// Compact quotes are sufficient for the board popup; full tables keep refreshChinaStocks.
export async function refreshChinaSimpleStocks(rows) {
  const codeOf = item => {
    const code = String(item.symbol || item.code || '').toLowerCase()
    return /\.(sh|sz|bj)$/.test(code) ? code.slice(-2) + code.slice(0, -3) : code
  }
  const codes = [...new Set(rows.map(codeOf).filter(code => /^(sh|sz|bj)\d{6}$/.test(code)))]
  if (!codes.length) return rows
  const quotes = await getStockQuotes(codes.map(code => 's_' + code).join(','))
  return rows.map(item => {
    const data = quotes['s_' + codeOf(item)]
    if (!Array.isArray(data) || !(quoteNumber(data[3]) > 0) || quoteNumber(data[5]) == null) return item
    return { ...item, name: data[1] || item.name, zxj: data[3], zdf: data[5] }
  })
}
export async function refreshChinaStocks(rows) {
  const codeOf = item => {
    const code = String(item.full_code || item.symbol || item.code || '').toLowerCase()
    return /\.(sh|sz|bj)$/.test(code) ? code.slice(-2) + code.slice(0, -3) : code
  }
  const codes = [...new Set(rows.map(codeOf).filter(code => /^(sh|sz|bj)\d{6}$/.test(code)))]
  const updates = {}
  if (codes.length) Object.assign(updates, await getStockQuotes(codes.join(',')))
  return rows.map(item => {
    const data = updates[codeOf(item)]
    if (!Array.isArray(data) || !(quoteNumber(data[3]) > 0)) return item
    const fields = { last_px:3, zxj:3, px_change:31, px_change_rate:32, zdf:32, amplitude:43, hand_rate:38, hsl:38, pe_rate:53, market_rate:46, lb:49, wb:74 }
    const value = { ...item }
    for (const [field, index] of Object.entries(fields)) if (quoteNumber(data[index]) != null) value[field] = data[index]
    // Match stockListByCate: turnover in 万元, capitalization in 亿元, shares in 万股.
    for (const [field, index, scale] of [['business_balance',37,1],['total_value',45,1],['circulation_value',44,1],['total_stock',73,0.0001],['total_mstock',76,0.0001]]) if (quoteNumber(data[index]) != null) value[field] = Number((Number(data[index]) * scale).toFixed(2))
    return value
  })
}
